import React, { useState } from "react";
import {
  Box,
  Flex,
  Text,
  Badge,
  Grid,
  GridItem,
  Divider,
  Image,
  Icon,
  useBreakpointValue,
  Tooltip,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
} from "@chakra-ui/react";
import { Images, Truck, Pencil, MessageCircle, Package, Package2 } from "lucide-react";
import NoDataFound from "components/NoDataGraphics/NoDataFound";
import UserCard from "components/Card/UserCard";
import ResponsiveTable from "components/Tables/InfoTable";
import InfoCard from "components/Card/InfoCard";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import Slider from "react-slick";
import getStatusColor from "../utils/StatusColor";
import ReleasePackage from "./ReleasePackage";
import BackgroundImage from "../../../../assets/img/back.png";
import { useHistory } from "react-router-dom";
import ReusableModal from "components/Modals/ReusableModal";
import { formatDate } from "utils/dateFormatter";

const ViewPackageDetails = ({ package: packageData, sender, recipient }) => {
  const [isReleaseModalOpen, setReleaseModalOpen] = useState(false);
  const [currentPackageData, setCurrentPackageData] = useState(packageData);
  const [isModalOpen, setModalOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const history = useHistory();

  const BASE_URL = `${process.env.REACT_APP_API_BASE_URL}/`.replace(/\/+$/, "/");


  const formatMoney = (value) => {
    const num = Number(value);
    return Number.isFinite(num) ? `P${num.toFixed(2)}` : "N/A";
  };

  const handleImageClick = (image) => {
    setSelectedImage(image);
    setModalOpen(true);
  };

  const CustomTab = ({ icon, label }) => (
    <Tab
      fontSize="sm"
      color="gray.500"
      _selected={{
        color: "white",
        bg: "transparent",
        backgroundImage: `url(${BackgroundImage})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
      }}
    >
      <Icon as={icon} mr={2} />
      {label}
    </Tab>
  );

  const headers = [
    { label: "Pickup", accessor: (data) => data.pickup_location },
    { label: "Dropoff", accessor: (data) => data.dropoff_location },
    { label: "Delivery Type", accessor: (data) => data.delivery_type },
    { label: "Status", accessor: (data) => data.package_status },
  ];

  const imageSize = useBreakpointValue({
    base: "100px",
    md: "150px",
    lg: "200px",
  });

  const sliderSettings = {
    dots: true,
    infinite: true,
    speed: 500,
    slidesToShow: 3,
    slidesToScroll: 1,
    autoplay: true,
    autoplaySpeed: 2000,
    appendDots: (dots) => (
      <Box display="flex" justifyContent="center" mt="10px">
        <ul style={{ margin: 0, padding: 0 }}>
          {dots.map((dot) => (
            <li
              key={dot.key}
              style={{ margin: "0 5px", cursor: "pointer" }}
              onClick={dot.props.onClick}
            >
              <Box
                width="10px"
                height="10px"
                borderRadius="50%"
                backgroundColor={
                  dot.props.className.includes("slick-active")
                    ? "#0e2254"
                    : "#ccc"
                }
              />
            </li>
          ))}
        </ul>
      </Box>
    ),
    responsive: [
      { breakpoint: 768, settings: { slidesToShow: 2 } },
      { breakpoint: 480, settings: { slidesToShow: 1 } },
    ],
  };

  return (
    <Flex direction="column" p={8} width="100%">
      <Tabs variant="enclosed">
        <TabList>
          <CustomTab icon={Package} label="Package Information" />
          <CustomTab icon={MessageCircle} label="Contacts" />
        </TabList>

        <TabPanels>
          <TabPanel>
            <InfoCard
              heading="Package Information"
              headingFontSize="md"
              headingFontWeight="500"
              headingColor="gray.500"
              icon={<Package2 />}
            >
              <Box mt={4}>
                <Flex justify="space-between" align="center" mb={5} wrap="wrap" gap={4}>
                  <Text fontSize="sm" fontWeight="500" color="gray.600">
                    {packageData?.package_description || "No description available"}
                  </Text>

                  <Text fontSize="sm" fontWeight="500" color="gray.500">
                    Delivery Price: {formatMoney(packageData?.price)}
                  </Text>

                  <Text fontSize="sm" fontWeight="500" color="gray.500">
                    Protection Fee: {formatMoney(packageData?.protection_fee)}
                  </Text>

                  <Text fontSize="sm" fontWeight="500" color="gray.500">
                    Estimated Value: {formatMoney(packageData?.estimated_value)}
                  </Text>

                  <Badge
                    colorScheme={getStatusColor(packageData?.package_status) || "gray"}
                  >
                    {packageData?.package_status || "No status available"}
                  </Badge>
                </Flex>

                <Grid templateColumns={{ base: "1fr", md: "1fr 1fr 1fr" }} gap={4}>
                  <GridItem>
                    <Text fontSize="sm" fontWeight="500" color="gray.600">
                      Category
                    </Text>
                    <Text fontSize="sm" color="gray.500">
                      {packageData?.size_category || "N/A"}
                    </Text>
                  </GridItem>

                  <GridItem>
                    <Text fontSize="sm" fontWeight="500" color="gray.600">
                      Description
                    </Text>
                    <Text fontSize="sm" color="gray.500">
                      {packageData?.package_weight || "N/A"}
                    </Text>
                    <Text fontSize="sm" color="gray.500">
                      {packageData?.package_length || "N/A"} x{" "}
                      {packageData?.package_width || "N/A"} x{" "}
                      {packageData?.package_height || "N/A"}
                    </Text>
                  </GridItem>

                  <GridItem>
                    <Text fontSize="sm" fontWeight="500" color="gray.600">
                      Last Updated
                    </Text>
                    <Text fontSize="sm" color="gray.500">
                      {packageData?.created_at
                        ? formatDate(packageData.created_at)
                        : "No date available"}
                    </Text>
                  </GridItem>
                </Grid>
              </Box>
            </InfoCard>

            <Divider my={6} />

            <InfoCard
              heading="Delivery Information"
              icon={<Truck />}
              headingFontSize="md"
              headingFontWeight="500"
              headingColor="gray.500"
            >
              <Box mt={4}>
                <ResponsiveTable headers={headers} data={packageData} />
              </Box>
            </InfoCard>

            <Divider my={6} />

            <InfoCard
              heading="Package Images"
              icon={<Images />}
              headingFontSize="md"
              headingFontWeight="500"
              headingColor="gray.500"
            >
              <Box mb={6}>
                {Array.isArray(packageData?.package_images) &&
                  packageData.package_images.length > 0 ? (
                  <Slider {...sliderSettings}>
                    {packageData.package_images.map((image, index) => (
                      <Box
                        key={index}
                        p={2}
                        m={1}
                        borderRadius="md"
                        display="flex"
                        justifyContent="center"
                        alignItems="center"
                        onClick={() => handleImageClick(image)}
                        cursor="pointer"
                      >
                        <Image
                          src={`${BASE_URL}${image}`}
                          alt={`Package Image ${index + 1}`}
                          boxSize={imageSize}
                          objectFit="cover"
                          borderRadius="md"
                          fallbackSrc="https://via.placeholder.com/150?text=Image+Unavailable"
                        />
                      </Box>
                    ))}
                  </Slider>
                ) : (
                  <NoDataFound
                    message="No package images uploaded"
                    fontWeight="bold"
                    fontSize="md"
                    width="100px"
                    height="100px"
                  />

                )}
              </Box>
            </InfoCard>
          </TabPanel>

          <TabPanel>
            <Grid templateColumns={{ base: "repeat(1, 1fr)", md: "repeat(2, 1fr)" }} gap={4}>
              <GridItem>
                <Text fontSize="sm" fontWeight="bold" mb={2} color="gray.600">
                  Sender
                </Text>
                <UserCard
                  avatar={sender?.avatar}
                  name={sender ? `${sender.first_name} ${sender.last_name}` : "N/A"}
                  email={sender?.email}
                  phone={sender?.phone_number}
                  cellphone={sender?.phone_number}
                  isLoading={false}
                  onChatClick={() =>
                    history.push(`/admin/messages?userId=${sender?.user_id}`)
                  }
                />
              </GridItem>

              <GridItem>
                <Text fontSize="sm" fontWeight="bold" mb={2} color="gray.600">
                  Recipient
                </Text>
                <UserCard
                  avatar={recipient?.avatar}
                  name={recipient ? `${recipient.first_name} ${recipient.last_name}` : "N/A"}
                  email={recipient?.email}
                  phone={recipient?.phone_number}
                  cellphone={recipient?.phone_number}
                  isLoading={false}
                  onChatClick={() =>
                    history.push(`/admin/messages?userId=${recipient?.user_id}`)
                  }
                />
              </GridItem>
            </Grid>
          </TabPanel>


        </TabPanels>
      </Tabs>

      <ReusableModal
        isOpen={isModalOpen}
        onClose={() => setModalOpen(false)}
        bodyMessage={
          selectedImage ? (
            <Image
              src={`${BASE_URL}${selectedImage}`}
              alt="Selected Package Image"
              maxH="500px"
              maxW="100%"
              objectFit="contain"
              mx="auto"
            />
          ) : null
        }
        showConfirmButton={false}
      />
    </Flex>
  );
};

export default ViewPackageDetails;